import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  IResponseApplication,
  OrderDTOListResponse,
  EmisorEmailDataResponseIEnumerableResponseDto,
  EmisorEmailDataResponse,
  ObjectResponse,
} from '@schemas-legacy/Response.interface';
import { backendTypes } from '@intelishs-group/schema-core';
import { FileUploaderService } from '../services/file-uploader.service';

@Injectable({
  providedIn: 'root',
})
export class FacturacionService {
  private http = inject(HttpClient);
  private fileUploaderService = inject(FileUploaderService);
  private apiUrl = environment.BaseUrl;

  /**
   * Verifica si una respuesta tiene errored: true y lanza el error con el mensaje del servicio
   * @param response Respuesta que puede contener errored, errorMessage y errors
   */
  private checkResponseError(response: {
    errored?: boolean;
    errorMessage?: string | null;
    errors?: string[] | null;
  }): void {
    if (response.errored) {
      const errorDetails = response.errors?.length ? `\n${response.errors.join('\n')}` : '';
      throw new Error(`${response.errorMessage ?? 'Error desconocido'}${errorDetails}`);
    }
  }

  /**
   * Extrae el mensaje de error del ObjectResponse cuando la petición HTTP falla
   * @param error Error capturado en el catch
   */
  private extractErrorMessage(error: any): never {
    if (error?.error) {
      const errorResponse = error.error as ObjectResponse;
      if (errorResponse.errorMessage) {
        const errorDetails = errorResponse.errors?.length
          ? `\n${errorResponse.errors.join('\n')}`
          : '';
        throw new Error(`${errorResponse.errorMessage}${errorDetails}`);
      }
    }

    throw error;
  }

  /**
   * Obtiene el PDF de una orden/factura por su ID
   * @param timbradoId ID del archivo PDF en el FileManager
   * @returns Blob con el contenido del PDF
   */
  obtenerPDFByTimbradoId(timbradoId: string): Promise<Blob> {
    return this.fileUploaderService.descargarArchivo(timbradoId);
  }

  /**
   * Obtiene los datos de clasificación/empresa interna por id de empresa
   * @param idEmpresa ID de la empresa
   */
  public async GetEmpresaInternaByIdeEmpresa(idEmpresa: number): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.http.get<IResponseApplication<any>>(
          `${this.apiUrl}/Company/GetEmpresaInternaByIdeEmpresa?idEmpresa=${idEmpresa}`,
        ),
      );

      this.checkResponseError(response);

      return response.object;
    } catch (error: any) {
      console.error('Error al obtener la empresa interna:', error);
      return null;
    }
  }

  /**
   * Obtiene las ordenes según el parámetro de búsqueda recibida
   * @param contenido el valor del contenido a buscar
   * @returns Lista de órdenes
   */
  async obtenerOrdenesPorBusqueda(
    contenido: string,
  ): Promise<backendTypes.components['schemas']['OrderDTO'][]> {
    try {
      const response = await lastValueFrom(
        this.http.get<OrderDTOListResponse>(
          `${this.apiUrl}/Billing/orders/searchstampinvoice?keysearch=${encodeURIComponent(contenido)}`,
        ),
      );

      this.checkResponseError(response);

      return response.object ?? [];
    } catch (error: any) {
      console.error('Error al obtener las órdenes por búsqueda:', error);
      return [];
    }
  }

  /**
   * Busca facturas/órdenes por término de búsqueda (consecutivo, UUID, RFC, etc.)
   * @param searchTerm Término de búsqueda
   * @returns Lista de órdenes que coinciden con la búsqueda
   */
  async buscarFacturas(
    searchTerm: string,
  ): Promise<backendTypes.components['schemas']['OrderDTO'][]> {
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        return [];
      }

      const response = await this.obtenerOrdenesPorBusqueda(searchTerm);

      const term = searchTerm.toLowerCase().trim();

      // Filtrar órdenes que coincidan con el término de búsqueda
      const filteredOrders = response.filter((order) => {
        return (
          (order.consecutivo && order.consecutivo.toLowerCase().includes(term)) ||
          (order.uuid && order.uuid.toLowerCase().includes(term)) ||
          (order.rfcReceptor && order.rfcReceptor.toLowerCase().includes(term)) ||
          (order.rfcEmisor && order.rfcEmisor.toLowerCase().includes(term)) ||
          (order.nombreEmisor && order.nombreEmisor.toLowerCase().includes(term)) ||
          (order.nombreReceptor && order.nombreReceptor.toLowerCase().includes(term)) ||
          (order.idOrder && order.idOrder.toString().includes(term))
        );
      });

      return filteredOrders;
    } catch (error: any) {
      console.error('Error al buscar facturas:', error);
      return [];
    }
  }

  /**
   * Obtiene todos los contactos asociados a un timbrado específico
   * @param idTimbrado ID del timbrado para obtener documentos asociados
   * @returns Lista de correos timbrados
   */
  async obtenerContactosPorIdTimbrado(idTimbrado: number): Promise<EmisorEmailDataResponse | null> {
    try {
      const response = await lastValueFrom(
        this.http.get<EmisorEmailDataResponseIEnumerableResponseDto>(
          `${this.apiUrl}/invoices/${idTimbrado}/email`,
        ),
      );

      this.checkResponseError(response);

      return (response.object as any as EmisorEmailDataResponse) ?? null;
    } catch (error: any) {
      console.error('Error al obtener contactos del timbrado:', error);
      this.extractErrorMessage(error);
    }
  }
}
